import VueRouter from "vue-router";
import { setPageTitle } from "@/helpers/pageTitle";

// Layout
const BaseLayout = () => import("@/views/_layouts/BaseLayout");

// Common Pages
const Comingsoon = () => import("@/views/_general/Comingsoon");
const Error404 = () => import("@/views/_general/404");
const ForgotPassword = () => import("@/views/_general/ForgotPassword");
const VerifyEmail = () => import("@/views/_general/VerifyEmail");
const Login = () => import("@/views/_general/Login");
const Register = () => import("@/views/_general/Register");
const ResetPassword = () => import("@/views/_general/ResetPassword");
const WebSocketTest = () => import("@/views/_general/WebSocketTest");

// Pages
const AuthProfile = () => import("@/views/_auth/AuthProfile");

const Home = () => import("@/views/_general/Home");

const UserList = () => import("@/views/Users/UserList");
const UserProfile = () => import("@/views/Users/UserProfile");

const SupportiveSingle = () => import("@/views/Supports/SupportiveSingle");

const FAQList = () => import("@/views/Supports/FAQ");

const AuditTrailList = () => import("@/views/AuditTrails/AuditTrailList");
const AuditTrailProfile = () => import("@/views/AuditTrails/AuditTrailProfile");

const AnnouncementList = () => import("@/views/Announcements/AnnouncementList");

const AdvertisementList = () => import("@/views/Advertisements/AdvertisementList");

const SettingPage = () => import("@/views/SystemVariables/SettingPage");
const SecuritySettingPage = () => import("@/views/SystemVariables/SecuritySettingPage");

const BroadcastList = () => import("@/views/Broadcasts/BroadcastList");

const RecyclableCategoryList = () => import('@/views/RecyclableCategory/RecyclableCategoryList')

const StaffList = () => import("@/views/Staffs/StaffList");

const AgentList = () => import("@/views/Agents/AgentList");

const LockUserList = () => import('@/views/Users/LockUserList')

const CenterLocationList = () => import("@/views/CenterLocations/CenterLocationList");
const CenterLocationInfo = () => import('@/views/CenterLocations/CenterLocationInfo')

// Recycling request
const RecyclingRequestList = () => import("@/views/RecyclingRequests/RecyclingRequestList");

// Voucher Pages
const VoucherList = () => import('@/views/Vouchers/VoucherList')
const VoucherInfo = () => import('@/views/Vouchers/VoucherInfo')

// User Voucher Pages
const UserVoucherList = () => import('@/views/UserVouchers/UserVoucherList')

// Pickup request
const PickupRequestList = () => import("@/views/PickupRequests/PickupRequestList");

// Pickup location
const PickupLocationList = () => import("@/views/PickupLocations/PickupLocationList");
const PickupLocationInfo = () => import('@/views/PickupLocations/PickupLocationInfo')

const KioskRequestList = () => import("@/views/KioskRequests/KioskRequestList");

const KioskList = () => import("@/views/Kiosks/KioskList");
const KioskInfo = () => import('@/views/Kiosks/KioskInfo')

const BrandOwnerList = () => import("@/views/BrandOwners/BrandOwnerList");
const BrandOwnerProfile = () => import("@/views/BrandOwners/BrandOwnerProfile");

const BrandCentreList = () => import("@/views/BrandCentres/BrandCentreList");

const OwnerBuybackRequestList = () => import("@/views/_vendor/BuybackRequests/BuybackRequestList");

const LocationOwnerList = () => import("@/views/LocationOwners/LocationOwnerList");
const LocationOwnerProfile = () => import("@/views/LocationOwners/LocationOwnerProfile");

// Reports
const ReportList = () => import("@/views/Reports/ReportList");

const DashboardChart = () => import("@/views/_vendor/Dashboards/DashboardChart");
const VendorUserList = () => import("@/views/_vendor/Users/UserList");
const VendorUserProfile = () => import("@/views/_vendor/Users/UserProfile");
const VendorKioskRequestList = () => import("@/views/_vendor/KioskRequests/KioskRequestList");
const VendorKioskList = () => import("@/views/_vendor/Kiosks/KioskList");
const VendorLocationList = () => import("@/views/_vendor/Locations/LocationList");
const VendorLocationInfo = () => import('@/views/_vendor/Locations/LocationInfo')
// Routes
// ** for more information, kindy refer to https://github.com/websanova/vue-auth/blob/master/docs/Privileges.md
// ** auth: true <- can be access only if auth.check = true
// ** auth: false <- cannot be access is auth.check = true
// ** auth: null, title: '' <- no auth rule
// ** auth: [ROLES.ADMIN, 2] <- can be access is auth.user.role matched
const ROLES = {
	ADMIN: 1,
	STAFF: 2,
	BRAND_OWNER: 4,
	LOCATION_OWNER: 6,
	AGENT: 7,
	SUPERADMIN: 8,
	BRAND_CENTRE: 9,
};

// Roles that have full admin access. Brand centre is NOT an admin — it is a staff-level
// user scoped to a single center location.
const ADMIN_ROLES = [ROLES.ADMIN, ROLES.SUPERADMIN];

const adminStaffRoutes = [
	{
		path: "/comingsoon",
		name: "admin.home2",
		component: Comingsoon,
		meta: { auth: true, role: [...ADMIN_ROLES, ROLES.STAFF, ROLES.BRAND_OWNER], title: "Coming Soon" },
	},
	{
		path: "/home",
		name: "home",
		component: Home,
		meta: { auth: true, role: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.STAFF, ROLES.BRAND_OWNER, ROLES.AGENT, ROLES.BRAND_CENTRE], title: "Home" },
	},

	// user
	{
		path: "/user/list",
		name: "user.list",
		component: UserList,
		meta: { auth: true, role: ADMIN_ROLES, title: "User List" },
	},
	{
		path: "/my/profile",
		name: "my.profile",
		component: AuthProfile,
		meta: { auth: true, role: [...ADMIN_ROLES, ROLES.STAFF], title: "Profile" },
	},
	{
		path: "/user/profile/:id",
		name: "user.profile",
		component: UserProfile,
		meta: { auth: true, role: ADMIN_ROLES, title: "User Profile" },
	},
	{
		path: "/user/profile",
		name: "user.my-profile",
		component: UserProfile,
		meta: { auth: true, role: ADMIN_ROLES, title: "My Profile" },
	},

	// support
	{
		path: "/support/terms-and-conditions",
		name: "support.terms-and-conditions",
		component: SupportiveSingle,
		meta: {
			auth: true,
			role: ADMIN_ROLES,
			title: "T&C",
			params: { type: "terms-and-conditions" },
		},
	},
	{
		path: "/support/terms-of-use",
		name: "support.terms-of-use",
		component: SupportiveSingle,
		meta: {
			auth: true,
			role: ADMIN_ROLES,
			title: "T&C",
			params: { type: "terms-of-use" },
		},
	},
	{
		path: "/support/privacy-policy",
		name: "support.privacy-policy",
		component: SupportiveSingle,
		meta: {
			auth: true,
			role: ADMIN_ROLES,
			title: "T&C",
			params: { type: "privacy-policy" },
		},
	},
	{
		path: "/support/faq",
		name: "support.faq",
		component: FAQList,
		meta: { auth: true, role: ADMIN_ROLES, title: "FAQ", params: { type: "faq" } },
	},
	{
		path: "/support/withdrawal-tnc",
		name: "support.withdrawal-tnc",
		component: SupportiveSingle,
		meta: {
			auth: true,
			role: ADMIN_ROLES,
			title: "Arbitrage Policy Update",
			params: { type: "withdrawal-tnc" },
		},
	},

	//audit trails
	{
		path: "/audit-trail/list",
		name: "audit-trail.list",
		component: AuditTrailList,
		meta: { auth: true, role: ADMIN_ROLES, title: "Audit Trail List" },
	},
	{
		path: "/audit-trail/profile/:id",
		name: "audit-trail.profile",
		component: AuditTrailProfile,
		meta: { auth: true, role: ADMIN_ROLES, title: "Audit Trail Profile" },
	},

	{
		path: "/announcement/list",
		name: "announcement.list",
		component: AnnouncementList,
		meta: { auth: true, role: ADMIN_ROLES, title: "Announcement List" },
	},

	{
		path: "/advertisement/list",
		name: "advertisement.list",
		component: AdvertisementList,
		meta: { auth: true, role: ADMIN_ROLES, title: "Advertisement List" },
	},

	{
		path: "/setting",
		name: "setting.page",
		component: SettingPage,
		meta: {
			auth: true,
			title: "Settings",
			role: ADMIN_ROLES,
			page_category: "setting",
		},
	},

	{
		path: "/security-setting",
		name: "security-setting.page",
		component: SecuritySettingPage,
		meta: {
			auth: true,
			title: "Security Settings",
			role: ADMIN_ROLES,
			page_category: "security-setting",
		},
	},

	// broadcast
	{
		path: "/broadcast/list",
		name: "broadcast.list",
		component: BroadcastList,
		meta: { auth: true, role: ADMIN_ROLES, title: "Broadcast list" },
	},

	// interest
	{ path: '/recyclable-category/list', name: 'recyclable_category.list', component: RecyclableCategoryList, meta: { auth: true, role: ADMIN_ROLES, title: 'Recyclable Category List' } },

	// staff
	{ path: '/staff/list', name: 'staff.list', component: StaffList, meta: { auth: true, role: ADMIN_ROLES, title: 'Staff List' } },
	
	// staff
	{ path: '/agent/list', name: 'agent.list', component: AgentList, meta: { auth: true, role: ADMIN_ROLES, title: 'Agent List' } },

	//locked-user
	{ path: '/locked-user/list', name: 'locked-user.list', component: LockUserList, meta: { auth: true, title: 'Lock User List', role: ADMIN_ROLES } },

	// center location
	{ path: '/location/list', name: 'location.list', component: CenterLocationList, meta: { auth: true, role: ADMIN_ROLES, title: 'Buyback Location & Pricing List' } },
	{
		path: '/center-location/info/:id',
		name: 'center-location.info',
		component: CenterLocationInfo,
		meta: { auth: true, role: ADMIN_ROLES, title: 'Center Location Info' }
	},

	// recycling request
	{ path: '/recycling-request/list', name: 'recycling_request.list', component: RecyclingRequestList, meta: { auth: true, role: [...ADMIN_ROLES, ROLES.STAFF, ROLES.BRAND_CENTRE], title: 'Buyback Request List' } },

	// user-voucher
	{
		path: '/user-voucher/list',
		name: 'user-voucher.list',
		component: UserVoucherList,
		meta: { auth: true, role: ADMIN_ROLES, title: 'User Voucher List' }
	},

	// voucher
	{
		path: '/voucher/list',
		name: 'voucher.list',
		component: VoucherList,
		meta: { auth: true, role: ADMIN_ROLES, title: 'Voucher List' }
	},
	{
		path: '/voucher/info/:id',
		name: 'voucher.info',
		component: VoucherInfo,
		meta: { auth: true, role: ADMIN_ROLES, title: 'Voucher Info' }
	},

	// reports
	{
		path: '/report/list',
		name: 'report.list',
		component: ReportList,
		meta: { auth: true, role: ADMIN_ROLES, title: 'Reports' }
	},

	// pickup request
	{ path: '/pickup-request/list', name: 'pickup_request.list', component: PickupRequestList, meta: { auth: true, role: [ROLES.ADMIN, ROLES.STAFF, ROLES.AGENT], title: 'Pickup Request List' } },
	{
		path: '/pickup-location/info/:id',
		name: 'pickup-location.info',
		component: PickupLocationInfo,
		meta: { auth: true, role: ADMIN_ROLES, title: 'Pickup Location Info' }
	},

	// pickup location
	{ path: '/pick-up-location/list', name: 'pickup-location.list', component: PickupLocationList, meta: { auth: true, role: ADMIN_ROLES, title: 'Pickup Location List' } },
	
	// kisok request
	{ path: '/kiosk-request/list', name: 'kiosk_request.list', component: KioskRequestList, meta: { auth: true, role: [...ADMIN_ROLES], title: 'Kiosk Request List' } },
	
	// kisok management
	{ path: '/kiosk/list', name: 'kiosk.list', component: KioskList, meta: { auth: true, role: ADMIN_ROLES, title: 'Kiosk List' } },
	{
		path: '/kiosk/info/:id',
		name: 'kiosk.info',
		component: KioskInfo,
		meta: { auth: true, role: ADMIN_ROLES, title: 'Kiosk Info' }
	},

	{
		path: "/brand-owner/list",
		name: "brand-owner.list",
		component: BrandOwnerList,
		meta: { auth: true, role: ADMIN_ROLES, title: "Brand Owner List", module: "brand_owners" },
	},
	{
		path: "/brand-owner/profile/:id",
		name: "brand-owner.profile",
		component: BrandOwnerProfile,
		meta: { auth: true, role: ADMIN_ROLES, title: "Brand Owner Profile", module: "brand_owners" },
	},
	{
		path: "/location-owner/list",
		name: "location-owner.list",
		component: LocationOwnerList,
		meta: { auth: true, role: ADMIN_ROLES, title: "Location Owner List", module: "location_owners" },
	},
	{
		path: "/location-owner/profile/:id",
		name: "location-owner.profile",
		component: LocationOwnerProfile,
		meta: { auth: true, role: ADMIN_ROLES, title: "Location Owner Profile", module: "location_owners" },
	},
];

const vendorRoutes = [
	{
		path: "/brand-owner/dashboard",
		name: "brand-owner.dashboard",
		component: DashboardChart,
		meta: { auth: true, role: [ROLES.BRAND_OWNER], title: "Dashboard" },
	},
	{
		path: "/brand-owner/tenant/list",
		name: "brand-owner-vendor.list",
		component: VendorUserList,
		meta: { auth: true, role: [ROLES.BRAND_OWNER, ...ADMIN_ROLES], title: "Tenant List", module: "users" },
	},
	{
		path: "/brand-owner/tenant/profile/:id",
		name: "brand-owner-vendor.profile",
		component: VendorUserProfile,
		meta: { auth: true, role: [ROLES.BRAND_OWNER, ...ADMIN_ROLES], title: "Tenant Profile", module: "users" },
	},
	{ path: '/brand-owner/tenant/recycling-request/list', name: 'brand-owner-recycling_request.list', component: VendorKioskRequestList, meta: { auth: true, role: [ROLES.BRAND_OWNER, ...ADMIN_ROLES], title: 'Recycling Request List' } },
	
	// { path: '/brand-owner/kiosk/list', name: 'brand-owner-kiosk.list', component: VendorKioskList, meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Kiosk List' } },
	// { path: '/brand-owner/location/list', name: 'brand-owner-location.list', component: VendorLocationList, meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Location & Pricing List' } },
	// {
	// 	path: '/brand-owner/location/info/:id',
	// 	name: 'brand-owner-location.info',
	// 	component: VendorLocationInfo,
	// 	meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Location Info' }
	// },
];

const locationOwnerRoutes = [
	{
		path: "/location-owner/dashboard",
		name: "location-owner.dashboard",
		component: DashboardChart,
		meta: { auth: true, role: [ROLES.BRAND_OWNER, ROLES.LOCATION_OWNER], title: "Dashboard" },
	},
	{
		path: "/location-owner/tenant/list",
		name: "location-owner-vendor.list",
		component: VendorUserList,
		props: { ownerType: 'location' },
		meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: "Tenant List", module: "users" },
	},
	{
		path: "/location-owner/tenant/profile/:id",
		name: "location-owner-vendor.profile",
		component: VendorUserProfile,
		meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: "Tenant Profile", module: "users" },
	},
	{
		path: "/location-owner/user/list",
		name: "location-owner-user.list",
		component: VendorUserList,
		meta: { auth: true, role: [ROLES.BRAND_OWNER, ...ADMIN_ROLES], title: "User List", module: "users" },
	},
	{
		path: "/location-owner/user/profile/:id",
		name: "location-owner-user.profile",
		component: VendorUserProfile,
		meta: { auth: true, role: [ROLES.BRAND_OWNER, ...ADMIN_ROLES], title: "User Profile", module: "users" },
	},
	{ path: '/location-owner/kiosk/recycling-request/list', name: 'location-owner-recycling_request.list', component: KioskRequestList, meta: { auth: true, role: [ROLES.BRAND_OWNER, ROLES.LOCATION_OWNER, ROLES.ADMIN], title: 'Kiosk Request List' } },
	
	{ path: '/location-owner/kiosk/list', name: 'location-owner-kiosk.list', component: KioskList, meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Kiosk List' } },
	{
		path: '/location-owner/kiosk/info/:id',
		name: 'location-owner-kiosk.info',
		component: KioskInfo,
		meta: { auth: true, role: [...ADMIN_ROLES, ROLES.LOCATION_OWNER], title: 'Kiosk Info' }
	},

	{ path: '/location-owner/location/list', name: 'location-owner-location.list', component: VendorLocationList, meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Location & Pricing List' } },
	{
		path: '/location-owner/location/info/:id',
		name: 'location-owner-location.info',
		component: VendorLocationInfo,
		meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Location Info' }
	},
]

// Buyback portal shared by brand owner (4) and location owner (6) when the super admin has
// enabled buyback for that owner. The backend additionally enforces buyback_enabled.
const ownerBuybackRoutes = [
	{
		path: '/location-owner/location/list',
		name: 'owner-location.list',
		component: VendorLocationList,
		meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Buyback Location & Pricing' },
	},
	{
		path: '/tenant/location/info/:id',
		name: 'owner-location.info',
		component: VendorLocationInfo,
		meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Location Info' },
	},
	{
		path: '/location-owner/brand-centre/list',
		name: 'owner-brand-centre.list',
		component: BrandCentreList,
		meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Brand Centre' },
	},
	{
		path: '/location-owner/buyback-request/list',
		name: 'owner-buyback-request.list',
		component: OwnerBuybackRequestList,
		meta: { auth: true, role: [ROLES.LOCATION_OWNER, ...ADMIN_ROLES], title: 'Buyback Request' },
	},
]

// Combine all authenticated routes
const baseLayoutRoutes = [
	...adminStaffRoutes,
	...vendorRoutes,
	...locationOwnerRoutes,
	...ownerBuybackRoutes,
];

const routes = [
	{
		path: "/",
		name: "login",
		component: Login,
		meta: { auth: null, title: "Login" },
	},
	{
		path: "/login",
		name: "login",
		component: Login,
		meta: { auth: null, title: "Login" },
	},
	{
		path: "/register/:code",
		name: "register",
		component: Register,
		meta: { auth: null, title: "Register" },
	},
	{
		path: "/password/reset/:token",
		name: "reset.password",
		component: ResetPassword,
		meta: { auth: null, title: "Reset Password" },
	},
	{
		path: "/forgot-password",
		name: "forgot.password",
		component: ForgotPassword,
		meta: { auth: null, title: "Forgot Password" },
	},
	{
		path: "/email-verify/:token",
		name: "verify.email",
		component: VerifyEmail,
		meta: { auth: null, title: "Verify Email" },
	},

	// base_layout
	{
		path: "/home",
		name: "base_layout",
		component: BaseLayout,
		children: baseLayoutRoutes,
	},

	{
		path: "*",
		name: "404",
		component: Error404,
		meta: { auth: null, title: "404" },
	},
];

const router = new VueRouter({
	history: true,
	mode: "history",
	routes,
});

router.beforeEach((to, from, next) => {
	setPageTitle(to.meta.title);
	next();
});

export default router;
